import React, { useState, useEffect } from "react";
import config from "../config.js";
import { apiErrorMessage } from '../utils/apiError';
import { emailSenderHeaders } from '../utils/emailSender';

// Admin / Expense Purchase Orders — non-inventory vendor POs (software subs,
// service contracts, maintenance plans). Fully separate from the material
// Purchase Orders page; talks to the isolated /admin_purchase_orders API.

const API_BASE = config.API_BASE_URL;
const STATUSES = ["Draft", "Issued", "Acknowledged", "Closed", "Cancelled"];
const BILLING = ["", "One-time", "Monthly", "Quarterly", "Semi-Annually", "Annually"];
const SIGNERS = ["", "Mike Sisk", "Jesse Sotta"];

const STATUS_BADGE = {
  Draft: "bg-secondary",
  Issued: "bg-primary",
  Acknowledged: "bg-info",
  Closed: "bg-success",
  Cancelled: "bg-dark",
};

function money(n) {
  const v = Number(n || 0);
  return "$" + v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function todayStr() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}
function dstr(v) {
  return v ? String(v).slice(0, 10) : "";
}

export default function AdminPurchaseOrdersPage() {
  const [issuers, setIssuers] = useState([]);
  const [pos, setPos] = useState([]);
  const [selected, setSelected] = useState(null); // full PO when editing
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ---- form fields ----
  const [issuerId, setIssuerId] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [status, setStatus] = useState("Draft");
  const [orderDate, setOrderDate] = useState(todayStr());
  const [vendorName, setVendorName] = useState("");
  const [vAddr1, setVAddr1] = useState("");
  const [vAddr2, setVAddr2] = useState("");
  const [vCity, setVCity] = useState("");
  const [vState, setVState] = useState("");
  const [vZip, setVZip] = useState("");
  const [vCountry, setVCountry] = useState("USA");
  const [vContact, setVContact] = useState("");
  const [vEmail, setVEmail] = useState("");
  const [vPhone, setVPhone] = useState("");
  const [vCustNo, setVCustNo] = useState("");
  const [quoteNo, setQuoteNo] = useState("");
  const [terms, setTerms] = useState("");
  const [svcStart, setSvcStart] = useState("");
  const [svcEnd, setSvcEnd] = useState("");
  const [billing, setBilling] = useState("");
  const [discountPct, setDiscountPct] = useState("0");
  const [taxAmt, setTaxAmt] = useState("0");
  const [miscAmt, setMiscAmt] = useState("0");
  const [notes, setNotes] = useState("");
  const [authorizedBy, setAuthorizedBy] = useState("");
  const [signedBy, setSignedBy] = useState("");
  const [lines, setLines] = useState([{ description: "", quantity: "1", uom: "EA", unit_price: "0" }]);

  useEffect(() => {
    loadIssuers();
    loadPOs();
  }, []);

  async function loadIssuers() {
    try {
      const res = await fetch(`${API_BASE}/admin_po_issuers`);
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const data = await res.json();
      setIssuers(data);
      if (data.length && !issuerId) setIssuerId(String(data[0].issuer_id));
    } catch (err) {
      setError(err.message);
    }
  }

  async function loadPOs() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin_purchase_orders`);
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      setPos(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setSelected(null);
    setPoNumber("");
    setStatus("Draft");
    setOrderDate(todayStr());
    setVendorName("");
    setVAddr1("");
    setVAddr2("");
    setVCity("");
    setVState("");
    setVZip("");
    setVCountry("USA");
    setVContact("");
    setVEmail("");
    setVPhone("");
    setVCustNo("");
    setQuoteNo("");
    setTerms("");
    setSvcStart("");
    setSvcEnd("");
    setBilling("");
    setDiscountPct("0");
    setTaxAmt("0");
    setMiscAmt("0");
    setNotes("");
    setAuthorizedBy("");
    setSignedBy("");
    setLines([{ description: "", quantity: "1", uom: "EA", unit_price: "0" }]);
    if (issuers.length) setIssuerId(String(issuers[0].issuer_id));
  }

  async function handleSelect(po) {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin_purchase_orders/${po.admin_po_id}`);
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const d = await res.json();
      setSelected(d);
      setIssuerId(String(d.issuer_id));
      setPoNumber(d.po_number || "");
      setStatus(d.status || "Draft");
      setOrderDate(dstr(d.order_date) || todayStr());
      setVendorName(d.vendor_name || "");
      setVAddr1(d.vendor_address_line1 || "");
      setVAddr2(d.vendor_address_line2 || "");
      setVCity(d.vendor_city || "");
      setVState(d.vendor_state || "");
      setVZip(d.vendor_zip || "");
      setVCountry(d.vendor_country || "");
      setVContact(d.vendor_contact_name || "");
      setVEmail(d.vendor_contact_email || "");
      setVPhone(d.vendor_contact_phone || "");
      setVCustNo(d.vendor_customer_number || "");
      setQuoteNo(d.vendor_quote_number || "");
      setTerms(d.payment_terms || "");
      setSvcStart(dstr(d.service_period_start));
      setSvcEnd(dstr(d.service_period_end));
      setBilling(d.billing_frequency || "");
      setDiscountPct(String(d.discount_percent == null ? 0 : d.discount_percent));
      setTaxAmt(String(d.tax_amount == null ? 0 : d.tax_amount));
      setMiscAmt(String(d.misc_amount == null ? 0 : d.misc_amount));
      setNotes(d.notes_to_vendor || "");
      setAuthorizedBy(d.authorized_by || "");
      setSignedBy(d.signed_by || "");
      const ls = (d.lines || []).map((l) => ({
        description: l.description || "",
        quantity: String(l.quantity == null ? 1 : l.quantity),
        uom: l.uom || "EA",
        unit_price: String(l.unit_price == null ? 0 : l.unit_price),
      }));
      setLines(ls.length ? ls : [{ description: "", quantity: "1", uom: "EA", unit_price: "0" }]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function updateLine(i, key, val) {
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, [key]: val } : l)));
  }
  function addLine() {
    setLines((prev) => [...prev, { description: "", quantity: "1", uom: "EA", unit_price: "0" }]);
  }
  function removeLine(i) {
    setLines((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev));
  }

  function computeTotals() {
    let subtotal = 0;
    lines.forEach((l) => {
      subtotal += (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0);
    });
    const disc = (subtotal * (parseFloat(discountPct) || 0)) / 100;
    const tax = parseFloat(taxAmt) || 0;
    const misc = parseFloat(miscAmt) || 0;
    return { subtotal, disc, tax, misc, total: subtotal - disc + tax + misc };
  }

  function buildPayload() {
    return {
      issuer_id: parseInt(issuerId, 10),
      po_number: poNumber.trim() || null,
      status,
      order_date: orderDate || null,
      vendor_name: vendorName.trim(),
      vendor_address_line1: vAddr1.trim() || null,
      vendor_address_line2: vAddr2.trim() || null,
      vendor_city: vCity.trim() || null,
      vendor_state: vState.trim() || null,
      vendor_zip: vZip.trim() || null,
      vendor_country: vCountry.trim() || null,
      vendor_contact_name: vContact.trim() || null,
      vendor_contact_email: vEmail.trim() || null,
      vendor_contact_phone: vPhone.trim() || null,
      vendor_customer_number: vCustNo.trim() || null,
      vendor_quote_number: quoteNo.trim() || null,
      payment_terms: terms.trim() || null,
      service_period_start: svcStart || null,
      service_period_end: svcEnd || null,
      billing_frequency: billing || null,
      discount_percent: parseFloat(discountPct) || 0,
      tax_amount: parseFloat(taxAmt) || 0,
      misc_amount: parseFloat(miscAmt) || 0,
      notes_to_vendor: notes.trim() || null,
      authorized_by: authorizedBy.trim() || null,
      signed_by: signedBy || null,
      lines: lines
        .filter((l) => l.description.trim())
        .map((l) => ({
          description: l.description.trim(),
          quantity: parseFloat(l.quantity) || 0,
          uom: (l.uom || "EA").trim() || "EA",
          unit_price: parseFloat(l.unit_price) || 0,
        })),
    };
  }

  async function handleSave() {
    if (!issuerId) {
      alert("Select an issuer.");
      return;
    }
    if (!vendorName.trim()) {
      alert("Vendor name is required.");
      return;
    }
    const payload = buildPayload();
    if (!payload.lines.length) {
      alert("Add at least one line item with a description.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      let savedId = selected ? selected.admin_po_id : null;
      if (selected) {
        const res = await fetch(`${API_BASE}/admin_purchase_orders/${selected.admin_po_id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(await apiErrorMessage(res));
      } else {
        const res = await fetch(`${API_BASE}/admin_purchase_orders`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(await apiErrorMessage(res));
        const d = await res.json();
        savedId = d.admin_po_id;
      }
      await loadPOs();
      if (savedId) await handleSelect({ admin_po_id: savedId });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete() {
    if (!selected) return;
    if (!window.confirm(`Delete admin PO ${selected.po_number}? This cannot be undone.`)) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin_purchase_orders/${selected.admin_po_id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      await loadPOs();
      resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function openPdf() {
    if (!selected) return;
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin_purchase_orders/${selected.admin_po_id}/pdf`);
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setError(err.message);
    }
  }

  async function emailToVendor() {
    if (!selected) return;
    const to = window.prompt("Send purchase order to email:", vEmail || "");
    if (to === null) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin_purchase_orders/${selected.admin_po_id}/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...emailSenderHeaders() },
        body: JSON.stringify({ to_email: to.trim() || null }),
      });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const d = await res.json();
      alert(d.message || "Emailed.");
      await loadPOs();
      await handleSelect({ admin_po_id: selected.admin_po_id });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const totals = computeTotals();

  return (
    <div style={{ display: "flex", height: "100vh" }}>
      {/* LEFT: PO list */}
      <div className="border-end p-3" style={{ width: "340px", flexShrink: 0, overflowY: "auto" }}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">Admin POs</h5>
          <button className="btn btn-sm btn-outline-primary" onClick={resetForm}>+ New</button>
        </div>
        <div className="text-muted mb-2" style={{ fontSize: "0.75rem" }}>
          Non-inventory vendor POs (subscriptions, service contracts). Separate from material POs.
        </div>
        {loading && <div>Loading…</div>}
        {error && <div className="alert alert-danger py-1 px-2" style={{ fontSize: "0.8rem" }}>{error}</div>}
        {!loading && pos.length === 0 && <div className="text-muted">No admin POs yet.</div>}
        {pos.map((p) => (
          <div
            key={p.admin_po_id}
            className={`p-2 border-bottom ${selected && selected.admin_po_id === p.admin_po_id ? "bg-primary-subtle" : ""}`}
            style={{ cursor: "pointer" }}
            onClick={() => handleSelect(p)}
          >
            <div className="d-flex justify-content-between">
              <span className="fw-bold">{p.po_number}</span>
              <span className={`badge ${STATUS_BADGE[p.status] || "bg-secondary"}`}>{p.status}</span>
            </div>
            <div style={{ fontSize: "0.85rem" }}>{p.vendor_name}</div>
            <div className="d-flex justify-content-between text-muted" style={{ fontSize: "0.75rem" }}>
              <span>{p.issuer_name}</span>
              <span>{money(p.total)}</span>
            </div>
            <div className="text-muted" style={{ fontSize: "0.7rem" }}>
              {p.order_date ? new Date(p.order_date).toLocaleDateString() : ""}
            </div>
          </div>
        ))}
      </div>

      {/* RIGHT: detail form */}
      <div className="flex-grow-1 p-4 overflow-auto">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">{selected ? `Editing ${selected.po_number}` : "New Admin PO"}</h4>
          <div>
            {selected && (
              <>
                <button className="btn btn-outline-secondary btn-sm me-2" onClick={openPdf}>View PDF</button>
                <button className="btn btn-outline-primary btn-sm me-2" onClick={emailToVendor} disabled={loading}>Email to Vendor</button>
                <button className="btn btn-outline-danger btn-sm" onClick={handleDelete} disabled={loading}>Delete</button>
              </>
            )}
          </div>
        </div>
        {error && <div className="alert alert-danger">{error}</div>}

        {/* Header row */}
        <div className="row mb-3">
          <div className="col-md-4">
            <label className="form-label">Issuer (Buyer)</label>
            <select className="form-select" value={issuerId} onChange={(e) => setIssuerId(e.target.value)}>
              {issuers.map((iss) => (
                <option key={iss.issuer_id} value={iss.issuer_id}>{iss.name}</option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">PO Number</label>
            <input className="form-control" placeholder="auto" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} />
          </div>
          <div className="col-md-2">
            <label className="form-label">Status</label>
            <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">PO Date</label>
            <input type="date" className="form-control" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} />
          </div>
        </div>

        {/* Vendor */}
        <h6 className="text-uppercase text-muted">Vendor</h6>
        <div className="row mb-2">
          <div className="col-md-8">
            <label className="form-label">Vendor Name</label>
            <input className="form-control" value={vendorName} onChange={(e) => setVendorName(e.target.value)} />
          </div>
          <div className="col-md-4">
            <label className="form-label">Customer # (our acct)</label>
            <input className="form-control" value={vCustNo} onChange={(e) => setVCustNo(e.target.value)} />
          </div>
        </div>
        <div className="row mb-2">
          <div className="col-md-6">
            <label className="form-label">Address Line 1</label>
            <input className="form-control" value={vAddr1} onChange={(e) => setVAddr1(e.target.value)} />
          </div>
          <div className="col-md-6">
            <label className="form-label">Address Line 2</label>
            <input className="form-control" value={vAddr2} onChange={(e) => setVAddr2(e.target.value)} />
          </div>
        </div>
        <div className="row mb-2">
          <div className="col-md-4">
            <label className="form-label">City</label>
            <input className="form-control" value={vCity} onChange={(e) => setVCity(e.target.value)} />
          </div>
          <div className="col-md-2">
            <label className="form-label">State</label>
            <input className="form-control" value={vState} onChange={(e) => setVState(e.target.value)} />
          </div>
          <div className="col-md-3">
            <label className="form-label">Zip</label>
            <input className="form-control" value={vZip} onChange={(e) => setVZip(e.target.value)} />
          </div>
          <div className="col-md-3">
            <label className="form-label">Country</label>
            <input className="form-control" value={vCountry} onChange={(e) => setVCountry(e.target.value)} />
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-md-4">
            <label className="form-label">Contact Name</label>
            <input className="form-control" value={vContact} onChange={(e) => setVContact(e.target.value)} />
          </div>
          <div className="col-md-4">
            <label className="form-label">Contact Email</label>
            <input type="email" className="form-control" value={vEmail} onChange={(e) => setVEmail(e.target.value)} />
          </div>
          <div className="col-md-4">
            <label className="form-label">Contact Phone</label>
            <input className="form-control" value={vPhone} onChange={(e) => setVPhone(e.target.value)} />
          </div>
        </div>

        {/* Terms */}
        <h6 className="text-uppercase text-muted">Terms</h6>
        <div className="row mb-3">
          <div className="col-md-3">
            <label className="form-label">Vendor Quote #</label>
            <input className="form-control" value={quoteNo} onChange={(e) => setQuoteNo(e.target.value)} />
          </div>
          <div className="col-md-3">
            <label className="form-label">Payment Terms</label>
            <input className="form-control" placeholder="Net 30 Days" value={terms} onChange={(e) => setTerms(e.target.value)} />
          </div>
          <div className="col-md-2">
            <label className="form-label">Service Start</label>
            <input type="date" className="form-control" value={svcStart} onChange={(e) => setSvcStart(e.target.value)} />
          </div>
          <div className="col-md-2">
            <label className="form-label">Service End</label>
            <input type="date" className="form-control" value={svcEnd} onChange={(e) => setSvcEnd(e.target.value)} />
          </div>
          <div className="col-md-2">
            <label className="form-label">Billing</label>
            <select className="form-select" value={billing} onChange={(e) => setBilling(e.target.value)}>
              {BILLING.map((b) => <option key={b} value={b}>{b || "—"}</option>)}
            </select>
          </div>
        </div>

        {/* Line items */}
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="text-uppercase text-muted mb-0">Line Items</h6>
          <button className="btn btn-sm btn-outline-secondary mb-1" onClick={addLine}>+ Add Line</button>
        </div>
        <div className="text-muted mb-1" style={{ fontSize: "0.72rem" }}>
          Tip: the first line of a description prints as the bold item title; following lines print as smaller detail.
        </div>
        <table className="table table-sm align-middle">
          <thead>
            <tr>
              <th style={{ width: "50%" }}>Description</th>
              <th style={{ width: "10%" }}>Qty</th>
              <th style={{ width: "10%" }}>UOM</th>
              <th style={{ width: "15%" }}>Unit Price</th>
              <th style={{ width: "12%" }}>Extended</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => {
              const ext = (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0);
              return (
                <tr key={i}>
                  <td>
                    <textarea className="form-control form-control-sm" rows={2} value={l.description}
                      onChange={(e) => updateLine(i, "description", e.target.value)} />
                  </td>
                  <td><input className="form-control form-control-sm" value={l.quantity}
                    onChange={(e) => updateLine(i, "quantity", e.target.value)} /></td>
                  <td><input className="form-control form-control-sm" value={l.uom}
                    onChange={(e) => updateLine(i, "uom", e.target.value)} /></td>
                  <td><input className="form-control form-control-sm" value={l.unit_price}
                    onChange={(e) => updateLine(i, "unit_price", e.target.value)} /></td>
                  <td className="text-end">{money(ext)}</td>
                  <td>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => removeLine(i)} disabled={lines.length <= 1}>×</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Charges + totals */}
        <div className="row">
          <div className="col-md-7">
            <div className="row mb-2">
              <div className="col-md-4">
                <label className="form-label">Discount %</label>
                <input className="form-control" value={discountPct} onChange={(e) => setDiscountPct(e.target.value)} />
              </div>
              <div className="col-md-4">
                <label className="form-label">Sales Tax ($)</label>
                <input className="form-control" value={taxAmt} onChange={(e) => setTaxAmt(e.target.value)} />
              </div>
              <div className="col-md-4">
                <label className="form-label">Misc Charges ($)</label>
                <input className="form-control" value={miscAmt} onChange={(e) => setMiscAmt(e.target.value)} />
              </div>
            </div>
          </div>
          <div className="col-md-5">
            <table className="table table-sm">
              <tbody>
                <tr><td>Subtotal</td><td className="text-end">{money(totals.subtotal)}</td></tr>
                <tr><td>Discount{parseFloat(discountPct) ? ` (${parseFloat(discountPct)}%)` : ""}</td>
                  <td className="text-end">({money(totals.disc)})</td></tr>
                <tr><td>Sales Tax</td><td className="text-end">{money(totals.tax)}</td></tr>
                <tr><td>Misc Charges</td><td className="text-end">{money(totals.misc)}</td></tr>
                <tr className="fw-bold table-light"><td>TOTAL</td><td className="text-end">{money(totals.total)}</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Notes + signature */}
        <div className="mb-3">
          <label className="form-label">Notes &amp; Instructions to Vendor</label>
          <textarea className="form-control" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder={"1. Reference this PO number on all invoices.\n2. ..."} />
        </div>
        <div className="row mb-3">
          <div className="col-md-4">
            <label className="form-label">Signature</label>
            <select className="form-select" value={signedBy} onChange={(e) => setSignedBy(e.target.value)}>
              {SIGNERS.map((s) => <option key={s} value={s}>{s || "— None (blank line) —"}</option>)}
            </select>
            <div className="form-text">Auto-renders a cursive signature + date on the PDF.</div>
          </div>
          <div className="col-md-8">
            <label className="form-label">Authorized By line (optional override)</label>
            <input className="form-control" value={authorizedBy} onChange={(e) => setAuthorizedBy(e.target.value)}
              placeholder="defaults to: {Signer}, {Issuer}" />
          </div>
        </div>

        <button className="btn btn-primary me-2" onClick={handleSave} disabled={loading}>
          {selected ? "Save Changes" : "Create Admin PO"}
        </button>
        {selected && (
          <button className="btn btn-outline-secondary" onClick={resetForm} disabled={loading}>Cancel</button>
        )}
      </div>
    </div>
  );
}
